import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, Card, List, Typography, Space, Tag, Badge, Button, Input, Select, message, Divider, Popconfirm, Avatar, Empty, Spin, Tooltip } from 'antd';
import { ShopOutlined, UserOutlined, ClockCircleOutlined, CheckCircleOutlined, SendOutlined, MessageOutlined, SearchOutlined, LoadingOutlined, ExclamationCircleTwoTone, DownOutlined, QuestionOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { MessageIcon } from '../../utils/icons';
import { fetchInboxMessages, updateMessageProcessStatus, sendCustomerMessage, loadMoreInboxMessages, setInboxSearchLoading, updateMessageInStore } from './actions';
import './Inbox.css';

// Enable dayjs timezone plugin
dayjs.extend(utc);
dayjs.extend(timezone);

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

// Simple message sentiment detection
const detectMessageSentiment = (messageContent) => {
    if (!messageContent) return null;
    
    const content = messageContent.toLowerCase();
    
    // Angry/Complaint - Highest priority
    const angryKeywords = ['terrible', 'awful', 'worst', 'hate', 'disappointed', 'mad', 'angry', 'frustrated', 'ridiculous', 'unacceptable', 'refund', 'cancel'];
    if (angryKeywords.some(keyword => content.includes(keyword))) {
        return { type: 'angry', icon: '😡', color: '#ff4d4f' };
    }
    
    // Urgent - High priority
    const urgentKeywords = ['urgent', 'asap', 'quickly', 'fast', 'emergency', 'right away', 'immediately', 'need help now', 'please respond'];
    if (urgentKeywords.some(keyword => content.includes(keyword))) {
        return { type: 'urgent', icon: '⚡', color: '#faad14' };
    }
    
    // Question - Medium priority
    const questionKeywords = ['how', 'what', 'when', 'where', 'why', 'can you', 'could you', 'help', 'question', '?'];
    if (questionKeywords.some(keyword => content.includes(keyword))) {
        return { type: 'question', icon: <QuestionOutlined />, color: '#1890ff' };
    }
    
    // Happy - Low priority
    const happyKeywords = ['love', 'amazing', 'excellent', 'perfect', 'wonderful', 'great', 'awesome', 'thank you', 'thanks', 'satisfied'];
    if (happyKeywords.some(keyword => content.includes(keyword))) {
        return { type: 'happy', icon: '😊', color: '#52c41a' };
    }
    
    return null;
};

// Component to display avatar with sentiment icon
const AvatarWithSentiment = ({ messageContent, isUserMessage, size = "default", style = {} }) => {
    const sentiment = !isUserMessage ? detectMessageSentiment(messageContent) : null;
    
    // If there's sentiment, use emoji as icon, otherwise use UserOutlined
    const avatarIcon = sentiment ? (
        <span style={{ fontSize: size === 'small' ? '14px' : '18px' }}>
            {sentiment.icon}
        </span>
    ) : (
        <UserOutlined />
    );
    
    return (
        <Tooltip title={sentiment ? `${sentiment.type} message` : ''}>
            <Avatar
                size={size}
                icon={avatarIcon}
                style={{
                    backgroundColor: isUserMessage ? '#1890ff' : (sentiment ? sentiment.color : '#87d068'),
                    ...style
                }}
            />
        </Tooltip>
    );
};

const Inbox = () => {
    const dispatch = useDispatch();
    const { messages, loading, selectedInbox, pagination, loadingMore, searchLoading } = useSelector(state => state.inbox);
    const { autoMessages } = useSelector(state => state.dataCommon);
    const [selectedInboxItem, setSelectedInboxItem] = useState(null);
    const [switchLoading, setSwitchLoading] = useState({});
    const [newMessage, setNewMessage] = useState('');
    const [sendingMessage, setSendingMessage] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    // Use autoMessages from Redux
    const availableTemplates = autoMessages || [];

    // Memoize dropdown options to prevent re-render issues
    const templateOptions = useMemo(() => {
        if (!availableTemplates || availableTemplates.length === 0) {
            return [
                {
                    label: '⚠️ Chưa có mẫu tin nhắn nào',
                    value: 'no_templates',
                    disabled: true
                }
            ];
        }

        return availableTemplates.map(template => ({
            label: `${template.description || 'Template'} - ${template.value?.substring(0, 40)}...`,
            value: template.key
        }));
    }, [availableTemplates]);

    // Initial load effect
    useEffect(() => {
        dispatch(fetchInboxMessages('', false)); // Use main loading for initial load
    }, [dispatch]);

    // Handle search on Enter key press
    const handleSearchKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            dispatch(fetchInboxMessages(searchTerm, true)); // Use search loading for search
        }
    };

    // Handle search clear
    const handleSearchClear = () => {
        setSearchTerm('');
        dispatch(fetchInboxMessages('', true)); // Use search loading when clearing
    };    // Load more messages when scrolling to bottom
    const handleLoadMore = useCallback(() => {
        if (!loadingMore && pagination && pagination.page < pagination.totalPages) {
            dispatch(loadMoreInboxMessages(pagination.page + 1, searchTerm));
        }
    }, [dispatch, loadingMore, pagination, searchTerm]);

    // Handle scroll event for infinite scroll
    const handleScroll = useCallback((e) => {
        const { target } = e;
        const threshold = 50; // Load more when 50px from bottom

        if (target.scrollTop + target.clientHeight >= target.scrollHeight - threshold) {
            handleLoadMore();
        }
    }, [handleLoadMore]);

    // Group messages by amazonOrderId (no filtering needed since backend handles search)
    const groupedMessages = useMemo(() => {
        if (!messages || !Array.isArray(messages)) return [];

        const grouped = messages.reduce((acc, message) => {
            const { amazonOrderId } = message;
            if (!acc[amazonOrderId]) {
                acc[amazonOrderId] = {
                    amazonOrderId,
                    messages: [],
                    latestMessage: null,
                    unprocessedCount: 0,
                    sellerAccountName: message.order?.sellerAccount?.name || 'Unknown Seller'
                };
            }

            acc[amazonOrderId].messages.push(message);

            // Set latest message
            if (!acc[amazonOrderId].latestMessage ||
                new Date(message.customerMessageDate || message.createdAt) >
                new Date(acc[amazonOrderId].latestMessage.customerMessageDate || acc[amazonOrderId].latestMessage.createdAt)) {
                acc[amazonOrderId].latestMessage = message;
            }

            // Count unprocessed customer messages
            if (message.messageType === 'customer_message' && !message.isProcessed) {
                acc[amazonOrderId].unprocessedCount++;
            }

            return acc;
        }, {});

        return Object.values(grouped);
    }, [messages]);

    // Get current conversation messages
    const currentConversation = useMemo(() => {
        if (!selectedInboxItem) return [];

        return selectedInboxItem.messages
            .filter(msg => msg.messageType === 'customer_message' || msg.messageType === 'user_message')
            .sort((a, b) => new Date(b.customerMessageDate || b.createdAt) - new Date(a.customerMessageDate || a.createdAt));
    }, [selectedInboxItem]);

    // Get order details for right panel
    const orderDetails = useMemo(() => {
        if (!selectedInboxItem) return null;
        return selectedInboxItem.messages[0]?.order || null;
    }, [selectedInboxItem]);

    const handleInboxSelect = (inboxItem) => {
        setSelectedInboxItem(inboxItem);
        setNewMessage('');
    };

    const handleProcessStatusChange = async (messageId, checked) => {
        setSwitchLoading(prev => ({ ...prev, [messageId]: true }));

        try {
            const result = await dispatch(updateMessageProcessStatus(messageId, checked));

            if (result.success) {
                message.success(checked ? 'Message marked as processed' : 'Message marked as unprocessed');

                // Update the selected inbox item messages in local state
                if (selectedInboxItem) {
                    const updatedMessages = selectedInboxItem.messages.map(msg =>
                        msg.id === messageId ? { ...msg, isProcessed: checked } : msg
                    );
                    setSelectedInboxItem({
                        ...selectedInboxItem,
                        messages: updatedMessages
                    });
                }

                // Redux store is already updated through updateMessageInStore action in updateMessageProcessStatus
                // No need to call fetchInboxMessages() anymore
                
            } else {
                throw new Error(result.message || 'Failed to update message status');
            }
        } catch (error) {
            console.error('Error updating message status:', error);
            message.error(error.message || 'Failed to update message status');
        } finally {
            setSwitchLoading(prev => ({ ...prev, [messageId]: false }));
        }
    };

    // Handle auto message template selection
    const handleTemplateSelect = useCallback((templateKey) => {
        const template = autoMessages?.find(msg => msg.key === templateKey);
        if (template && template.value) {
            // Sử dụng value của template (type auto_messages sẽ có value là text content)
            setNewMessage(template.value);
            message.success('Template đã được chèn vào tin nhắn');
        }
    }, [autoMessages]);

    const handleSendMessage = async () => {
        if (!newMessage.trim() || !selectedInboxItem || !orderDetails) {
            message.error('Please enter a message');
            return;
        }

        setSendingMessage(true);
        try {
            const result = await dispatch(sendCustomerMessage({
                orderId: orderDetails.id,
                amazonOrderId: selectedInboxItem.amazonOrderId,
                messageContent: newMessage.trim()
            }));

            if (result.success) {
                message.success('Message sent successfully');
                setNewMessage('');

                // Add the sent message to current conversation immediately for better UX
                const sentMessage = {
                    id: `temp-${Date.now()}`,
                    messageType: 'user_message',
                    messageContent: newMessage.trim(),
                    createdAt: new Date().toISOString(),
                    isProcessed: true,
                    processedAt: new Date().toISOString()
                };

                if (selectedInboxItem) {
                    setSelectedInboxItem({
                        ...selectedInboxItem,
                        messages: [sentMessage, ...selectedInboxItem.messages]
                    });
                }

                // Instead of full refresh, we could add the message to Redux store
                // For now, let's keep it simple and just update local state
                // The next time user refreshes or navigates, they'll get fresh data
            } else {
                throw new Error(result.message || 'Failed to send message');
            }
        } catch (error) {
            console.error('Error sending message:', error);
            message.error(error.message || 'Failed to send message');
        } finally {
            setSendingMessage(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Unknown date';
        return dayjs(dateString).format('DD/MM/YYYY HH:mm');
    };

    const formatDatePDT = (dateString) => {
        if (!dateString) return '-';
        return dayjs(dateString).tz('America/Los_Angeles').format('MMM DD, YYYY HH:mm [PDT]');
    };

    // Format date similar to OrdersList with time and warnings
    const formatDateWithWarnings = (dateString, type, orderDetails) => {
        if (!dateString) return <span>-</span>;

        const usTime = dayjs(dateString).tz('America/Los_Angeles');
        const today = dayjs();
        const todayUS = dayjs().tz('America/Los_Angeles');

        let showWarning = false;
        let warningMessage = '';

        if (type === 'purchase') {
            // Purchase date formatting similar to OrdersList
            return (
                <div style={{ lineHeight: 1.2 }}>
                    <span style={{ fontSize: 13, fontWeight: 500 }}>
                        {usTime.format('DD/MM/YYYY')}
                    </span> <span style={{ fontSize: 10, color: '#666' }}>
                        ({usTime.format('hh:mm A')} PT)
                    </span>
                </div>
            );
        } else if (type === 'ship') {
            // Latest Ship Date warnings
            const shipDate = dayjs(dateString);
            const daysDiff = shipDate.diff(today, 'day');
            const isT2 = daysDiff <= 2;
            const isShipmentConfirmed = orderDetails?.isConfirmShipped === true;
            const isMFN = orderDetails?.fulfillmentChannel === 'MFN';
            const orderStatus = orderDetails?.orderStatus;
            const isShippedOrCanceled = orderStatus === 'Shipped' || orderStatus === 'Canceled';

            showWarning = isT2 && !isShipmentConfirmed && isMFN && !isShippedOrCanceled;
            warningMessage = 'Cần xác nhận shipment';

        } else if (type === 'delivery') {
            // Latest Delivery Date warnings
            const deliveryDateStr = usTime.format('YYYY-MM-DD');
            const todayStr = todayUS.format('YYYY-MM-DD');
            const daysDiff = dayjs(deliveryDateStr).diff(dayjs(todayStr), 'day');
            const isWithin2Days = daysDiff <= 2;
            const isMFN = orderDetails?.fulfillmentChannel === 'MFN';
            const orderStatus = orderDetails?.orderStatus;

            showWarning = isWithin2Days && isMFN && orderStatus !== 'Canceled';
            warningMessage = 'Sắp tới ngày giao hàng';
        }

        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <div style={{ lineHeight: 1.2 }}>
                    <div style={{
                        fontSize: 13,
                        fontWeight: 500,
                        color: showWarning ? 'red' : 'inherit'
                    }}>
                        {usTime.format('DD/MM/YYYY')}
                    </div>
                    {/* {type === 'purchase' && (
                        <div style={{ fontSize: 10, color: '#666' }}>
                            {usTime.format('hh:mm A')} PT
                        </div>
                    )} */}
                </div>
                {showWarning && (
                    <Tooltip title={warningMessage}>
                        <ExclamationCircleTwoTone twoToneColor='red' style={{ fontSize: 12 }} />
                    </Tooltip>
                )}
            </div>
        );
    };

    const truncateMessage = (text, maxLength = 100) => {
        if (!text) return '';
        return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    };

    const unprocessedCount = useMemo(() => {
        return currentConversation.filter(msg => msg.messageType === 'customer_message' && !msg.isProcessed).length;
    }, [currentConversation]);

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div className="inbox-container" style={{ height: 'calc(100vh - 35px)', padding: '16px' }}>
            
            <Row gutter={16} style={{ height: '100%' }}>
                {/* Left Panel - Inbox List */}
                <Col span={6} style={{ height: '100%' }}>
                    <Card
                        title={
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <MessageIcon style={{ color: '#1890ff', fontSize: '16px' }} />
                                <span>Customer Inbox</span>
                                <Badge count={groupedMessages.reduce((sum, item) => sum + item.unprocessedCount, 0)} />
                            </div>
                        }
                        style={{ height: '100%' }}
                        bodyStyle={{ padding: 0, height: 'calc(100% - 58px)', overflow: 'hidden' }}
                    >
                        <div style={{ padding: '16px 16px 8px 16px' }}>
                            <Input
                                placeholder="Search by order ID or message... (Press Enter to search)"
                                prefix={<SearchOutlined />}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onPressEnter={handleSearchKeyPress}
                                allowClear
                                onClear={handleSearchClear}
                            />
                        </div>

                        <div
                            style={{ height: 'calc(100% - 60px)', overflowX: 'hidden', overflowY: 'auto', position: 'relative' }}
                            onScroll={handleScroll}
                        >
                            <Spin spinning={searchLoading} tip="Searching messages...">
                                {groupedMessages.length === 0 && !searchLoading ? (
                                    <Empty
                                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                                        description="No messages found"
                                        style={{ marginTop: '40px' }}
                                    />
                                ) : (
                                    <>
                                        <List
                                            dataSource={groupedMessages}
                                            // split={false}
                                            renderItem={(item) => (
                                                <List.Item
                                                    onClick={() => handleInboxSelect(item)}
                                                    style={{
                                                        cursor: 'pointer',
                                                        padding: '12px 16px',
                                                        backgroundColor: selectedInboxItem?.amazonOrderId === item.amazonOrderId ? '#f0f8ff' : 'transparent',
                                                        borderLeft: selectedInboxItem?.amazonOrderId === item.amazonOrderId ? '3px solid #1890ff' : '3px solid transparent'
                                                    }}
                                                    className="inbox-list-item"
                                                >
                                                    <List.Item.Meta
                                                        avatar={
                                                            <Badge count={item.unprocessedCount} size="small">
                                                                <AvatarWithSentiment
                                                                    messageContent={item.latestMessage?.messageContent}
                                                                    isUserMessage={item.latestMessage?.messageType === 'user_message'}
                                                                />
                                                            </Badge>
                                                        }
                                                        title={
                                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                <Text strong style={{ fontSize: '13px' }}>
                                                                    #{item.amazonOrderId}
                                                                </Text>
                                                                <Text type="secondary" style={{ fontSize: '11px' }}>
                                                                    {formatDate(item.latestMessage?.customerMessageDate || item.latestMessage?.createdAt)}
                                                                </Text>
                                                            </div>
                                                        }
                                                        description={
                                                            <div>
                                                                <Text ellipsis={{ rows: 1 }} style={{ fontSize: '12px' }}>
                                                                    {truncateMessage(item.latestMessage?.messageContent)}
                                                                </Text>
                                                                <Text type="secondary" style={{ fontSize: '11px', display: 'block' }}>
                                                                    <ShopOutlined /> {item.sellerAccountName}
                                                                </Text>
                                                            </div>
                                                        }
                                                    />
                                                </List.Item>
                                            )}
                                        />

                                        {/* Loading more indicator */}
                                        {loadingMore && (
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                padding: '16px',
                                                color: '#999'
                                            }}>
                                                <Spin
                                                    indicator={<LoadingOutlined style={{ fontSize: 16 }} spin />}
                                                    style={{ marginRight: 8 }}
                                                />
                                                Loading more messages...
                                            </div>
                                        )}

                                        {/* End of list indicator */}
                                        {pagination && pagination.page >= pagination.totalPages && groupedMessages.length > 0 && (
                                            <div style={{
                                                textAlign: 'center',
                                                padding: '16px',
                                                color: '#999',
                                                fontSize: '12px'
                                            }}>
                                                No more messages to load
                                            </div>
                                        )}
                                    </>
                                )}
                            </Spin>
                        </div>
                    </Card>
                </Col>

                {/* Center Panel - Conversation */}
                <Col span={14} style={{ height: '100%' }}>
                    <Card
                        title={
                            selectedInboxItem ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <MessageIcon style={{ color: '#1890ff', fontSize: '16px' }} />
                                    <span>Conversation - #{selectedInboxItem.amazonOrderId}</span>
                                    {unprocessedCount > 0 && (
                                        <Badge
                                            count={unprocessedCount}
                                            style={{
                                                backgroundColor: '#ff4d4f',
                                                fontSize: '12px',
                                                height: '18px',
                                                lineHeight: '18px',
                                                minWidth: '18px'
                                            }}
                                        />
                                    )}
                                    <span style={{ color: '#999', fontSize: '14px', marginLeft: '8px' }}>
                                        ({currentConversation.length} messages)
                                    </span>
                                </div>
                            ) : (
                                <span>Select a conversation</span>
                            )
                        }
                        style={{ height: '100%' }}
                        bodyStyle={{ padding: 0, height: 'calc(100% - 58px)', display: 'flex', flexDirection: 'column' }}
                    >
                        {!selectedInboxItem ? (
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                <Empty
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    description="Select a conversation to view messages"
                                />
                            </div>
                        ) : (
                            <>
                                {/* Messages Area */}
                                <div style={{
                                    flex: 1,
                                    padding: '8px',
                                    overflowY: 'auto',
                                    backgroundColor: '#fafafa',
                                    backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(140, 140, 140, 0.1) 1px, transparent 0)',
                                    backgroundSize: '15px 15px'
                                }}>
                                    {currentConversation.length === 0 ? (
                                        <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                                            <MessageIcon style={{ fontSize: '48px', marginBottom: '16px', color: '#d9d9d9' }} />
                                            <div>No messages found for this conversation</div>
                                        </div>
                                    ) : (
                                        <List
                                            dataSource={currentConversation}
                                            split={false}
                                            renderItem={(msg, index) => {
                                                const isUserMessage = msg.messageType === 'user_message';

                                                return (
                                                    <List.Item
                                                        key={msg.id || index}
                                                        style={{
                                                            padding: '8px 16px',
                                                            border: 'none',
                                                            display: 'flex',
                                                            justifyContent: isUserMessage ? 'flex-end' : 'flex-start'
                                                        }}
                                                    >
                                                        <div style={{
                                                            maxWidth: '70%',
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            alignItems: isUserMessage ? 'flex-end' : 'flex-start'
                                                        }}>
                                                            {/* Message Header */}
                                                            <div style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 6,
                                                                marginBottom: '4px',
                                                                flexDirection: isUserMessage ? 'row-reverse' : 'row'
                                                            }}>
                                                                <AvatarWithSentiment
                                                                    messageContent={msg.messageContent}
                                                                    isUserMessage={isUserMessage}
                                                                    size="small"
                                                                />
                                                                <Text strong style={{ fontSize: '12px' }}>
                                                                    {isUserMessage ? 'You' : 'Customer'}
                                                                </Text>
                                                                <Text type="secondary" style={{ fontSize: '10px' }}>
                                                                    {formatDate(msg.customerMessageDate || msg.createdAt)}
                                                                </Text>
                                                            </div>

                                                            {/* Message Bubble */}
                                                            <div style={{
                                                                backgroundColor: isUserMessage ? '#1890ff' : '#f0f0f0',
                                                                color: isUserMessage ? '#fff' : '#000',
                                                                padding: '8px 12px',
                                                                borderRadius: '12px',
                                                                borderTopLeftRadius: isUserMessage ? '12px' : '4px',
                                                                borderTopRightRadius: isUserMessage ? '4px' : '12px',
                                                                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                                                                position: 'relative',
                                                                wordWrap: 'break-word',
                                                                wordBreak: 'break-word'
                                                            }}>
                                                                <Paragraph
                                                                    style={{
                                                                        margin: 0,
                                                                        fontSize: '14px',
                                                                        whiteSpace: 'pre-wrap',
                                                                        color: isUserMessage ? '#fff' : '#000'
                                                                    }}
                                                                >
                                                                    {msg.messageContent}
                                                                </Paragraph>
                                                            </div>

                                                            {/* Message Status and Actions */}
                                                            <div style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 8,
                                                                marginTop: '4px',
                                                                flexDirection: isUserMessage ? 'row-reverse' : 'row'
                                                            }}>
                                                                <Tag
                                                                    icon={isUserMessage ? <CheckCircleOutlined /> :
                                                                        (msg.isProcessed ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> :
                                                                            <ClockCircleOutlined style={{ color: '#faad14' }} />)
                                                                    }
                                                                    color={isUserMessage ? 'blue' : (msg.isProcessed ? 'success' : 'warning')}
                                                                    style={{
                                                                        fontSize: '10px',
                                                                        margin: 0,
                                                                        borderRadius: '8px',
                                                                        border: 'none'
                                                                    }}
                                                                    size="small"
                                                                >
                                                                    {isUserMessage ? 'Sent' : (msg.isProcessed ? 'Processed' : 'Pending')}
                                                                </Tag>

                                                                {!isUserMessage && (
                                                                    <Button
                                                                        type="text"
                                                                        size="small"
                                                                        loading={switchLoading[msg.id]}
                                                                        onClick={() => handleProcessStatusChange(msg.id, !msg.isProcessed)}
                                                                        style={{
                                                                            fontSize: '10px',
                                                                            height: '24px',
                                                                            padding: '0 6px',
                                                                            color: msg.isProcessed ? '#52c41a' : '#faad14',
                                                                            border: `1px solid ${msg.isProcessed ? '#52c41a' : '#faad14'}`
                                                                        }}
                                                                    >
                                                                        {msg.isProcessed ? 'Unprocess' : 'Process'}
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </List.Item>
                                                );
                                            }}
                                        />
                                    )}
                                </div>

                                {/* Message Input Area */}
                                <div style={{
                                    padding: '12px 16px',
                                    borderTop: '1px solid #e8e8e8',
                                    backgroundColor: '#fff'
                                }}>
                                    <div style={{ marginBottom: '8px' }}>
                                        <Text style={{ fontSize: '12px', color: '#666' }}>
                                            Send message to customer
                                        </Text>
                                    </div>
                                   
                                    <div style={{
                                        display: 'flex',
                                        gap: '8px',
                                        alignItems: 'flex-end'
                                    }}>
                                        <div style={{ flex: 1 }}>
                                            <TextArea
                                                value={newMessage}
                                                onChange={(e) => setNewMessage(e.target.value)}
                                                placeholder="Type your message..."
                                                maxLength={2000}
                                                rows={3}
                                                style={{
                                                    borderRadius: '8px',
                                                    border: '1px solid #d9d9d9',
                                                    resize: 'none'
                                                }}
                                                onPressEnter={(e) => {
                                                    if (e.shiftKey) return; // Allow shift+enter for new line
                                                    e.preventDefault();
                                                    if (newMessage.trim()) {
                                                        handleSendMessage();
                                                    }
                                                }}
                                            />
                                        </div>
                                        <Popconfirm
                                            title="Send message to customer?"
                                            description="Are you sure you want to send this message?"
                                            onConfirm={handleSendMessage}
                                            okText="Send"
                                            cancelText="Cancel"
                                            disabled={!newMessage.trim() || sendingMessage}
                                        >
                                            <Button
                                                type="primary"
                                                icon={<SendOutlined />}
                                                loading={sendingMessage}
                                                disabled={!newMessage.trim()}
                                                style={{
                                                    borderRadius: '50%',
                                                    width: '40px',
                                                    height: '40px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}
                                            />
                                        </Popconfirm>
                                    </div>
                                    <div style={{ marginTop: '4px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        {/* Auto Message Templates Dropdown */}
                                        <Select
                                            placeholder="Select template"
                                            style={{ minWidth: 180}}
                                            size="small"
                                            allowClear
                                            value={undefined} // Always reset after selection
                                            onSelect={handleTemplateSelect}
                                            onClear={() => message.info('Đã xóa lựa chọn template')}
                                            popupMatchSelectWidth={400}
                                            placement='topLeft'
                                            options={autoMessages && autoMessages.length > 0 ? 
                                                autoMessages.map(template => ({
                                                    value: template.key,
                                                    label: template.description || template.key
                                                })) : 
                                                [{ value: 'no_templates', label: '⚠️ Chưa có mẫu tin nhắn', disabled: true }]
                                            }
                                            notFoundContent="Không tìm thấy mẫu tin nhắn"
                                        />
                                        
                                        <Text type="secondary" style={{ fontSize: '10px' }}>
                                            {newMessage.length}/2000 • Press Enter to send
                                        </Text>
                                    </div>
                                </div>
                            </>
                        )}
                    </Card>
                </Col>

                {/* Right Panel - Order Details */}
                <Col span={4} style={{ height: '100%' }}>
                    <Card
                        title="Order Details"
                        style={{ height: '100%' }}
                        bodyStyle={{ height: 'calc(100% - 58px)', overflowY: 'auto' }}
                    >
                        {!orderDetails ? (
                            <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                                <div>Select a conversation to view order details</div>
                            </div>
                        ) : (
                            <div>
                                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                                    <div>
                                        <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>Amazon Order ID</Text>
                                        <Text strong style={{ fontSize: '14px' }}>#{orderDetails.amazonOrderId}</Text>
                                    </div>

                                    <div>
                                        <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>Purchase Date</Text>
                                        <div style={{ fontSize: '14px' }}>{formatDateWithWarnings(orderDetails.purchaseDate, 'purchase', orderDetails)}</div>
                                    </div>

                                    <div>
                                        <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>Latest Ship Date</Text>
                                        <div style={{ fontSize: '14px' }}>{formatDateWithWarnings(orderDetails.latestShipDate, 'ship', orderDetails)}</div>
                                    </div>

                                    <div>
                                        <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>Latest Delivery Date</Text>
                                        <div style={{ fontSize: '14px' }}>{formatDateWithWarnings(orderDetails.latestDeliveryDate, 'delivery', orderDetails)}</div>
                                    </div>

                                    <Divider style={{ margin: '16px 0' }} />

                                    <div>
                                        <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>Order Status</Text>
                                        <Tag color="blue" style={{ fontSize: '12px' }}>{orderDetails.orderStatus}</Tag>
                                    </div>

                                    <div>
                                        <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>Fulfillment Channel</Text>
                                        <Tag color="green" style={{ fontSize: '12px' }}>{orderDetails.fulfillmentChannel}</Tag>
                                    </div>

                                    <div>
                                        <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>Seller Account</Text>
                                        <Text style={{ fontSize: '14px' }}>{orderDetails.sellerAccount?.name || 'Unknown'}</Text>
                                    </div>

                                    {orderDetails.orderTotalAmount && (
                                        <div>
                                            <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>Order Total</Text>
                                            <Text strong style={{ fontSize: '14px', color: '#52c41a' }}>
                                                ${parseFloat(orderDetails.orderTotalAmount).toFixed(2)}
                                            </Text>
                                        </div>
                                    )}

                                    <Divider style={{ margin: '16px 0' }} />

                                    {/* Customer Information Section */}
                                    <div>
                                        <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                                            <UserOutlined style={{ marginRight: '4px' }} />
                                            Customer Information
                                        </Text>
                                        
                                        {orderDetails.shipName && (
                                            <div style={{ marginBottom: '8px' }}>
                                                <Text type="secondary" style={{ fontSize: '11px', display: 'block' }}>Name</Text>
                                                <Text style={{ fontSize: '13px' }}>{orderDetails.shipName}</Text>
                                            </div>
                                        )}

                                        {/* Shipping Address */}
                                        {(orderDetails.shipAddressLine1 || orderDetails.shipCity || orderDetails.shipStateOrRegion) && (
                                            <div style={{ marginBottom: '8px' }}>
                                                <Text type="secondary" style={{ fontSize: '11px', display: 'block' }}>Shipping Address</Text>
                                                <div style={{ fontSize: '13px', lineHeight: '1.4' }}>
                                                    {orderDetails.shipAddressLine1 && <div>{orderDetails.shipAddressLine1}</div>}
                                                    {orderDetails.shipAddressLine2 && <div>{orderDetails.shipAddressLine2}</div>}
                                                    {orderDetails.shipAddressLine3 && <div>{orderDetails.shipAddressLine3}</div>}
                                                    <div>
                                                        {[
                                                            orderDetails.shipCity,
                                                            orderDetails.shipCounty,
                                                            orderDetails.shipDistrict,
                                                            orderDetails.shipStateOrRegion,
                                                            orderDetails.shipPostalCode
                                                        ].filter(Boolean).join(', ')}
                                                    </div>
                                                    {orderDetails.shipCountryCode && (
                                                        <div>{orderDetails.shipCountryCode}</div>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {/* Phone Number - Blurred by default */}
                                        {orderDetails.shipPhone && (
                                            <div style={{ marginBottom: '8px' }}>
                                                <Text type="secondary" style={{ fontSize: '11px', display: 'block' }}>Phone</Text>
                                                <Tooltip title="Hover to view phone number">
                                                    <div className="blurred-phone" style={{ fontSize: '13px' }}>
                                                        {orderDetails.shipPhone}
                                                    </div>
                                                </Tooltip>
                                            </div>
                                        )}

                                        {orderDetails.shipEmail && (
                                            <div style={{ marginBottom: '8px' }}>
                                                <Text type="secondary" style={{ fontSize: '11px', display: 'block' }}>Email</Text>
                                                <Text style={{ fontSize: '13px' }}>{orderDetails.shipEmail}</Text>
                                            </div>
                                        )}
                                    </div>
                                </Space>
                            </div>
                        )}
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default Inbox;